import { type ReactNode, Suspense, lazy } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import NavBar from "./components/NavBar";
import { AuthProvider } from "./context/AuthContext";
import {
  BrandProvider,
  DEFAULT_BRAND_CODE,
  readBrandCookie,
} from "./context/BrandContext";
import CarDetail from "./pages/CarDetail";
import Filters from "./pages/Filters";
import Inventory from "./pages/Inventory";
import Login from "./pages/Login";
import Verify from "./pages/Verify";

// ECharts is heavy — keep it out of the inventory bundle.
const Depreciation = lazy(() => import("./pages/Depreciation"));
const Scatter = lazy(() => import("./pages/Scatter"));
const ColourMix = lazy(() => import("./pages/ColourMix"));
const PriceVsAge = lazy(() => import("./pages/PriceVsAge"));
const TimeOnLot = lazy(() => import("./pages/TimeOnLot"));
const BestValue = lazy(() => import("./pages/BestValue"));
const Efficiency = lazy(() => import("./pages/Efficiency"));
const RangeVsPrice = lazy(() => import("./pages/RangeVsPrice"));
const RangeVsPriceByVariant = lazy(() => import("./pages/RangeVsPriceByVariant"));
const EfficiencyVsPrice = lazy(() => import("./pages/EfficiencyVsPrice"));
const EfficiencyVsPriceByVariant = lazy(
  () => import("./pages/EfficiencyVsPriceByVariant"),
);
const PriceSpread = lazy(() => import("./pages/PriceSpread"));
const RangeSpread = lazy(() => import("./pages/RangeSpread"));
const StockTurn = lazy(() => import("./pages/StockTurn"));
const MarkdownCurve = lazy(() => import("./pages/MarkdownCurve"));
const PriceChanges = lazy(() => import("./pages/PriceChanges"));
const FloorPrice = lazy(() => import("./pages/FloorPrice"));
const AboutPriceBreakdown = lazy(() => import("./pages/AboutPriceBreakdown"));

function AnalyticsFallback() {
  return <p className="p-6 text-gray-500">Loading chart…</p>;
}

/** Resolve brand for the bare-path redirect: cookie → DEFAULT_BRAND_CODE. */
function resolveBareRedirectBrand(): string {
  return readBrandCookie() ?? DEFAULT_BRAND_CODE;
}

function RedirectToBrand({ suffix }: { suffix: string }) {
  const brand = resolveBareRedirectBrand();
  return <Navigate to={`/${brand}${suffix}`} replace />;
}

function RedirectCarToBrand() {
  const params = useParams<{ id: string }>();
  const brand = resolveBareRedirectBrand();
  return <Navigate to={`/${brand}/cars/${params.id}`} replace />;
}

/** Wraps brand-scoped routes — supplies `urlBrand` to BrandProvider. */
function BrandScope({ children }: { children: ReactNode }) {
  const params = useParams<{ brand: string }>();
  return <BrandProvider urlBrand={params.brand}>{children}</BrandProvider>;
}

/**
 * Wrapper for cross-brand top-level pages (§2.5.9, §2.5.10).
 *
 * These pages live at /analytics/{path} with no /{brand}/ prefix. They
 * must outrank the /:brand/* pattern in the outer Routes — otherwise
 * React Router 6's score-based matcher would consume "analytics" as
 * the brand parameter and bounce the user to /{cookieBrand}/cars via
 * the brand-scoped catch-all. Lifting these to top-level static routes
 * gives them the (10 + 10) score they need to win.
 */
function CrossBrandPage({ children }: { children: ReactNode }) {
  return (
    <BrandProvider>
      <NavBar />
      <Suspense fallback={<AnalyticsFallback />}>{children}</Suspense>
    </BrandProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* §2.5.9 + §2.5.10 cross-brand pages — top-level static
              paths so they outrank /:brand/* in React Router's score
              ranking (otherwise /analytics/efficiency-vs-price matches
              /:brand/* with :brand="analytics" and falls through the
              brand-scoped catch-all). */}
          <Route
            path="/analytics/efficiency-vs-price"
            element={
              <CrossBrandPage>
                <EfficiencyVsPrice />
              </CrossBrandPage>
            }
          />
          <Route
            path="/analytics/efficiency-vs-price-by-variant"
            element={
              <CrossBrandPage>
                <EfficiencyVsPriceByVariant />
              </CrossBrandPage>
            }
          />
          {/* Tech score is cross-brand — the residual ranking only means
              something across the whole fleet, not one brand at a time. */}
          <Route
            path="/analytics/efficiency"
            element={
              <CrossBrandPage>
                <Efficiency />
              </CrossBrandPage>
            }
          />
          <Route
            path="/analytics/price-spread"
            element={
              <CrossBrandPage>
                <PriceSpread />
              </CrossBrandPage>
            }
          />
          <Route
            path="/analytics/range-spread"
            element={
              <CrossBrandPage>
                <RangeSpread />
              </CrossBrandPage>
            }
          />
          <Route
            path="/analytics/stock-turn"
            element={
              <CrossBrandPage>
                <StockTurn />
              </CrossBrandPage>
            }
          />
          <Route
            path="/analytics/markdown-curve"
            element={
              <CrossBrandPage>
                <MarkdownCurve />
              </CrossBrandPage>
            }
          />
          <Route
            path="/analytics/price-changes"
            element={
              <CrossBrandPage>
                <PriceChanges />
              </CrossBrandPage>
            }
          />
          {/* Floor price exists in BOTH sections (§2.5.13): this is the
              cross-brand "All brands" view; the brand-scoped twin lives at
              /:brand/analytics/floor-price below. */}
          <Route
            path="/analytics/floor-price"
            element={
              <CrossBrandPage>
                <FloorPrice />
              </CrossBrandPage>
            }
          />

          {/* Brand-scoped routes — wrap in BrandScope so NavBar/pages know the active brand. */}
          <Route
            path="/:brand/*"
            element={
              <BrandScope>
                <NavBar />
                <Suspense fallback={<AnalyticsFallback />}>
                  <Routes>
                    <Route path="cars" element={<Inventory />} />
                    <Route path="cars/:id" element={<CarDetail />} />
                    <Route
                      path="analytics/depreciation"
                      element={<Depreciation />}
                    />
                    <Route path="analytics/scatter" element={<Scatter />} />
                    <Route
                      path="analytics/colour-mix"
                      element={<ColourMix />}
                    />
                    <Route
                      path="analytics/price-vs-age"
                      element={<PriceVsAge />}
                    />
                    <Route
                      path="analytics/time-on-lot"
                      element={<TimeOnLot />}
                    />
                    <Route path="best-value" element={<BestValue />} />
                    <Route
                      path="analytics/range-vs-price"
                      element={<RangeVsPrice />}
                    />
                    <Route
                      path="analytics/range-vs-price-by-variant"
                      element={<RangeVsPriceByVariant />}
                    />
                    {/* Floor price also has a brand-scoped twin (§2.5.13) —
                        auto-scoped to the active brand. */}
                    <Route
                      path="analytics/floor-price"
                      element={<FloorPrice brandScoped />}
                    />
                    {/* Cross-brand views live at top-level paths; strays
                        under /{brand}/... strip the prefix. */}
                    <Route
                      path="analytics/efficiency"
                      element={
                        <Navigate to="/analytics/efficiency" replace />
                      }
                    />
                    <Route
                      path="analytics/price-spread"
                      element={
                        <Navigate to="/analytics/price-spread" replace />
                      }
                    />
                    <Route
                      path="analytics/range-spread"
                      element={
                        <Navigate to="/analytics/range-spread" replace />
                      }
                    />
                    <Route
                      path="analytics/stock-turn"
                      element={
                        <Navigate to="/analytics/stock-turn" replace />
                      }
                    />
                    <Route
                      path="analytics/markdown-curve"
                      element={
                        <Navigate to="/analytics/markdown-curve" replace />
                      }
                    />
                    <Route
                      path="analytics/price-changes"
                      element={
                        <Navigate to="/analytics/price-changes" replace />
                      }
                    />
                    <Route
                      path="analytics/efficiency-vs-price"
                      element={
                        <Navigate to="/analytics/efficiency-vs-price" replace />
                      }
                    />
                    <Route
                      path="analytics/efficiency-vs-price-by-variant"
                      element={
                        <Navigate
                          to="/analytics/efficiency-vs-price-by-variant"
                          replace
                        />
                      }
                    />
                    <Route path="*" element={<RedirectToBrand suffix="/cars" />} />
                  </Routes>
                </Suspense>
              </BrandScope>
            }
          />

          {/* Brand-agnostic *content* pages — top-level statics for exactly
              the reason the §2.5.9/§2.5.10 block above is: React Router 6
              scores `/:brand/*` (dynamic + splat) above the brand-agnostic
              `/*` catch-all, so a single-segment path like `/login` matched
              with :brand="login" and fell straight through the brand-scoped
              `path="*"` to RedirectToBrand — the page never rendered and the
              user landed on /{brand}/cars. That silently made sign-in
              impossible (and with it every per-user feature), because /verify
              — the magic link's own target — was swallowed the same way.
              Nested declarations inside `/*` cannot win this; the route has
              to be a top-level static to score (10 + 10). */}
          {/* Bare /cars/{id} — emitted by every chart tooltip's "View detail"
              link and by the alert emails — has to be top-level for the same
              scoring reason as /login above: nested under `/*` it never
              matched, because `/:brand/*` (score 5) beat `/*` (score 1) and
              consumed "cars" as the brand, dropping the user on
              /{cookieBrand}/cars — the inventory list — instead of the car.
              As a static + dynamic path this scores 17 and wins outright. */}
          <Route path="/cars/:id" element={<RedirectCarToBrand />} />
          <Route path="/login" element={<CrossBrandPage><Login /></CrossBrandPage>} />
          <Route path="/verify" element={<CrossBrandPage><Verify /></CrossBrandPage>} />
          <Route path="/filters" element={<CrossBrandPage><Filters /></CrossBrandPage>} />
          <Route
            path="/about/price-breakdown"
            element={
              <CrossBrandPage>
                <AboutPriceBreakdown />
              </CrossBrandPage>
            }
          />

          {/* Brand-agnostic routes — no /{brand} prefix. */}
          <Route
            path="/*"
            element={
              <BrandProvider>
                <NavBar />
                <Suspense fallback={<AnalyticsFallback />}>
                  <Routes>
                    {/* login / verify / filters / about are top-level statics
                        in the outer block — see the comment there. Declaring
                        them here too would be dead weight, not a fallback. */}
                    {/* Bare brand-scoped paths redirect to the active brand. */}
                    <Route
                      path="cars"
                      element={<RedirectToBrand suffix="/cars" />}
                    />
                    <Route
                      path="best-value"
                      element={<RedirectToBrand suffix="/best-value" />}
                    />
                    <Route
                      path="analytics/depreciation"
                      element={
                        <RedirectToBrand suffix="/analytics/depreciation" />
                      }
                    />
                    <Route
                      path="analytics/scatter"
                      element={<RedirectToBrand suffix="/analytics/scatter" />}
                    />
                    <Route
                      path="analytics/price-vs-age"
                      element={
                        <RedirectToBrand suffix="/analytics/price-vs-age" />
                      }
                    />
                    <Route
                      path="analytics/time-on-lot"
                      element={
                        <RedirectToBrand suffix="/analytics/time-on-lot" />
                      }
                    />
                    <Route
                      path="analytics/range-vs-price"
                      element={
                        <RedirectToBrand suffix="/analytics/range-vs-price" />
                      }
                    />
                    <Route
                      path="analytics/range-vs-price-by-variant"
                      element={
                        <RedirectToBrand suffix="/analytics/range-vs-price-by-variant" />
                      }
                    />
                    {/* §2.5.9 + §2.5.10 routes are top-level statics —
                        see the outer Routes block. No entries needed here. */}
                    <Route path="" element={<RedirectToBrand suffix="/cars" />} />
                  </Routes>
                </Suspense>
              </BrandProvider>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
